import { PayloadAction } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { selectBaseApiUrl } from '../../../core/config-loader/store/config-loader-slice';
import { addNewGalleryTag, deleteGalleryTag, editGalleryTag, fetchGalleryTags, restoreGalleryTag } from '../../http/gallery-tags-api';
import { GalleryTag } from '../../interfaces/gallery-tag.interface';
import { galleryTagsActions } from '../galleryTagsSlice';

function* fetchGalleryTagsSaga(): SagaIterator {
  try {
    const baseApiUrl: string = yield select(selectBaseApiUrl);
    const galleryTagsResponse: { galleryTags: GalleryTag[] } = yield call(fetchGalleryTags, baseApiUrl);

    yield put(galleryTagsActions.fetchGalleryTagsSuccess(galleryTagsResponse));
  } catch (error) {
    Sentry.captureException(error);
    yield put(galleryTagsActions.fetchGalleryTagsError());
  }
}

function* editGalleryTagSaga({ payload }: PayloadAction<{ galleryTagId: number; editedGalleryTagName: string }>): SagaIterator {
  try {
    const baseApiUrl: string = yield select(selectBaseApiUrl);
    yield call(editGalleryTag, baseApiUrl, payload.galleryTagId, payload.editedGalleryTagName);

    yield put(galleryTagsActions.editGalleryTagSuccess({ id: payload.galleryTagId, editedGalleryTagName: payload.editedGalleryTagName }));
  } catch (error) {
    Sentry.captureException(error);
    yield put(galleryTagsActions.editGalleryTagError());
  }
}

function* restoreGalleryTagSaga({ payload }: PayloadAction<{ galleryTagId: number; galleryTagName: string }>): SagaIterator {
  try {
    const baseApiUrl: string = yield select(selectBaseApiUrl);
    yield call(restoreGalleryTag, baseApiUrl, payload.galleryTagId, payload.galleryTagName);

    yield put(galleryTagsActions.restoreGalleryTagSuccess({ galleryTagId: payload.galleryTagId }));
  } catch (error) {
    Sentry.captureException(error);
    yield put(galleryTagsActions.restoreGalleryTagError());
  }
}

function* deleteGalleryTagSaga({ payload }: PayloadAction<{ galleryTagId: number }>): SagaIterator {
  try {
    const baseApiUrl: string = yield select(selectBaseApiUrl);
    yield call(deleteGalleryTag, baseApiUrl, payload.galleryTagId);

    yield put(galleryTagsActions.deleteGalleryTagSuccess({ galleryTagId: payload.galleryTagId }));
  } catch (error) {
    Sentry.captureException(error);
    yield put(galleryTagsActions.deleteGalleryTagError());
  }
}

function* addNewGalleryTagSaga({ payload }: PayloadAction<{ newGalleryTagName: string }>): SagaIterator {
  try {
    const baseApiUrl: string = yield select(selectBaseApiUrl);
    const newGalleryTagResponse: { galleryTags: GalleryTag } = yield call(addNewGalleryTag, baseApiUrl, payload.newGalleryTagName);

    yield put(galleryTagsActions.addNewGalleryTagSuccess({ newGalleryTag: newGalleryTagResponse.galleryTags }));
  } catch (error) {
    Sentry.captureException(error);
    yield put(galleryTagsActions.addNewGalleryTagError());
  }
}

export function* galleryTagsSaga() {
  yield takeLatest(galleryTagsActions.fetchGalleryTags.type, fetchGalleryTagsSaga);
  yield takeLatest(galleryTagsActions.editGalleryTag.type, editGalleryTagSaga);
  yield takeLatest(galleryTagsActions.deleteGalleryTag.type, deleteGalleryTagSaga);
  yield takeLatest(galleryTagsActions.restoreGalleryTag.type, restoreGalleryTagSaga);
  yield takeLatest(galleryTagsActions.addNewGalleryTag.type, addNewGalleryTagSaga);
}

export default galleryTagsSaga;
